<template>
  <div class="site-header-container">
    <header
      class="site-header"
      :class="{
        'site-header--transparent': isTransparentHeader
      }"
    >
      <div class="site-header__container container">
        <!-- Left -->
        <div class="site-header__left">
          <button
            v-if="mobile.handle"
            class="
              site-header__menu
              icon-button
            "
            type="button"
            @click.prevent="handleMenuToggle"
          >
            <span
              class="visually-hidden"
              v-text="$string('general.site_header.menu')"
            />

            <icon-menu />
          </button>

          <button
            class="
              site-header__search
              icon-button
            "
            type="button"
            @click="openSearch"
          >
            <span
              class="visually-hidden"
              v-text="$string('general.site_header.search')"
            />

            <icon-search />
          </button>
        </div>

        <div class="site-header__center">
          <!-- Logo -->
          <component
            :is="titleElement"
            v-if="logo"
            class="site-header__logo-wrapper"
          >
            <a
              class="site-header__logo"
              :href="$variable('routes.root')"
            >
              <span
                class="visually-hidden"
                v-text="$variable('store.shopName')"
              />

              <!-- eslint-disable-next-line -->
              <img
                class="site-header__logo-image"
                :alt="$variable('store.shopName')"
                height="150"
                :src="logo"
                width="24"
              >
            </a>
          </component>

          <!-- Desktop Navigation -->
          <nav class="site-header__nav">
            <ul class="site-header__nav-list">
              <li
                v-for="(tier1) of desktop.items"
                :key="tier1.handle"
                class="site-header__nav-item"
              >
                <a
                  class="
                    site-header__nav-link
                    text-body-s-bold
                  "
                  :href="tier1.url"
                  :style="headerLinkStyle(linkStyles, tier1.title)"
                  @click.prevent="handleNavOpen(tier1)"
                  v-text="tier1.title"
                />
              </li>
            </ul>
          </nav>
        </div>

        <!-- Right -->
        <div class="site-header__right">
          <search-bar
            :is-transparent-header="isTransparentHeader"
            @update-search-query="(query) => dropdownSearch.query = query"
          />

          <a
            class="
              site-header__account
              icon-button
            "
            :href="$variable('routes.account.url')"
          >
            <span
              class="visually-hidden"
              v-text="$string('general.site_header.account')"
            />

            <icon-account />
          </a>

          <a
            class="
              site-header__cart
              icon-button
            "
            :href="$variable('routes.cart.url')"
            @click="handleCartToggle"
          >
            <span
              class="visually-hidden"
              v-text="$string('general.site_header.cart')"
            />

            <icon-cart />

            <span
              v-if="cart.item_count"
              class="
                site-header__cart-badge
                critical-clear
                text-body-2xs-regular
              "
              aria-hidden="true"
              v-text="cart.item_count"
            />
          </a>
        </div>
      </div>
    </header>

    <!-- Mega-nav -->
    <overlay
      key="megaNav"
      block-class="mega-nav-overlay"
      direction="down"
      namespace="megaNav"
      :template="false"
      type="shelf"
    >
      <template #body="overlay">
        <mega-nav
          :menu="megaNav.currentMenu"
          :menu-handle="megaNav.currentHandle"
          :mega-nav-content="megaNavContent"
          :link-styles="linkStyles"
          :overlay="overlay"
        />
      </template>
    </overlay>

    <!-- Menu drawer -->
    <teleport
      v-if="mobile.handle"
      to="body"
    >
      <overlay
        key="menuDrawer"
        block-class="menu-drawer-overlay"
        direction="right"
        namespace="menuDrawer"
        type="drawer"
      >
        <template #body="overlay">
          <menu-drawer
            v-if="getComponentVisible('menu-drawer')"
            v-cloak
            :additional-links="additionalLinks"
            :handle="mobile.handle"
            :item-count="mobile.itemCount"
            :overlay="overlay"
            :link-styles="linkStyles"
          />

          <div
            v-if="!getComponentActive('menu-drawer')"
            class="navigation__menu-drawer"
          >
            <div
              v-for="(_, index) of [...Array(mobile.itemCount)]"
              :key="`menu-drawer-loading-${index}`"
              class="navigation__menu-drawer-link"
            >
              <span class="navigation__menu-drawer-loading" />
            </div>
          </div>
        </template>
      </overlay>
    </teleport>

    <!-- Header search overlay -->
    <header-search
      :query="dropdownSearch.query"
    />

    <!-- Header search mobile overlay -->
    <header-search-mobile />
  </div>
</template>

<script>

/**
 * Vue: Site header (site-header)
 * -----------------------------------------------------------------------------
 * Site header central banner containing logo, icon buttons, and menu drawer.
 * - No style import as it's included in critical stylesheet.
 *
 * @param {Object} desktop - Desktop linklist handle and items.
 * @param {Object} linkStyles - Link style object.
 * @param {Object} liquid - Formatted Liquid object for section.
 * @param {String} logoBlack - Logo black image.
 * @param {String} logoWhite - Logo white image.
 * @param {Object} mobile - Mobile linklist handle and item count.
 * @param {Object} meganavContent - Mega-nav content.
 * @param {String} titleElement - HTML element of site logo based on page.
 *
 */
// eslint-disable-next-line import/named
import { defineAsyncComponent } from 'vue'
import { mapActions, mapGetters, mapState } from 'vuex'
import { headerLinkStyle } from '~/helpers/general'

import Overlay from '~global/overlay/overlay'
import SearchBar from '~async/search-bar/search-bar'
import HeaderSearch from '~async/header-search/header-search'
import HeaderSearchMobile from '~async/header-search-mobile/header-search-mobile'

import IconAccount from '~icons/general/account.svg'
import IconCart from '~icons/general/cart.svg'
import IconMenu from '~icons/general/menu.svg'
import IconSearch from '~icons/general/search.svg'

export default {
  name: 'SiteHeader',

  components: {
    IconAccount,
    IconCart,
    IconMenu,
    IconSearch,
    MenuDrawer: defineAsyncComponent({
      loader: () => import(
        /* webpackChunkName: 'component.menu-drawer' */
        '~async/menu-drawer/menu-drawer'
      ),
    }),
    MegaNav: defineAsyncComponent({
      loader: () => import(
        /* webpackChunkName: 'component.mega-nav' */
        '~global/mega-nav/mega-nav'
      ),
    }),
    HeaderSearch,
    HeaderSearchMobile,
    Overlay,
    SearchBar,
  },

  props: {
    // Needed for theme editor events
    // eslint-disable-next-line vue/no-unused-properties
    liquid: {
      type: Object,
      default: () => ({
        sectionId: '',
        isTransparentHeader: false,
      }),
    },
    logoBlack: {
      type: String,
      default: '',
    },
    logoWhite: {
      type: String,
      default: '',
    },
    mobile: {
      type: Object,
      default: () => ({
        handle: 'main-menu',
        itemCount: 5,
      }),
    },
    desktop: {
      type: Object,
      default: () => ({
        handle: 'main-menu',
        items: [],
      }),
    },
    additionalLinks: {
      type: Array,
      default: () => [],
    },
    linkStyles: {
      type: Object,
      default: () => ({}),
    },
    titleElement: {
      type: String,
      default: 'h1',
      validator: (value) => {
        return ['h1', 'span'].includes(value)
      },
    },
    megaNavContent: {
      type: Object,
      default: () => ({
        items: [],
      }),
    },
  },

  data() {
    return {
      navTimeout: null,
      megaNav: {
        currentMenu: null,
        currentHandle: '',
        open: false,
      },
      dropdownSearch: {
        query: '',
      },
    }
  },

  computed: {

    /**
     * Map Vuex getters.
     */
    ...mapGetters({
      getComponentExists: 'components/getExists',
    }),

    /**
     * Map Vuex state.
     */
    ...mapState({
      headerState: (state) => state.index.scroll.header,
      cart: (state) => state.cart.response,
      isMobile: (state) => state.index.screen.mobile,
    }),

    /**
     * Compute logo URL.
     * @returns {String}
     */
    logo() {
      if (this.isTransparentHeader && this.logoWhite) {
        return this.logoWhite
      }

      return this.logoBlack
    },

    /**
     * Compute if the header should be transparent based on the Header State
     * and current scroll position.
     * @returns {Boolean}
     */
    isTransparentHeader() {
      if (this.headerState !== 'open' || window.scrollY > 0) {
        return false
      }

      return this.liquid.isTransparentHeader
    },
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      closeOverlay: 'overlays/close',
      openOverlay: 'overlays/open',
      toggleOverlay: 'overlays/toggle',
    }),

    openSearch() {
      this.openOverlay({
        namespace: 'searchShelfMobile',
        ignoreDismissed: true,
      })
    },

    /**
     * Link style.
     */
    headerLinkStyle,

    /**
     * Handle menu toggle.
     */
    handleMenuToggle() {
      this.toggleOverlay({
        component: 'menu-drawer',
        ignoreDismissed: true,
        namespace: 'menuDrawer',
      })
    },

    /**
     * Handle cart toggle.
     * - Prevents opening cart drawer on cart page.
     * @param {Object} event - Click event.
     */
    handleCartToggle(event) {
      if (
        this.$variable('page.type') === 'cart' ||
        !this.getComponentExists('cart-drawer')
      ) {
        return
      }

      event.preventDefault()

      this.toggleOverlay({
        component: 'cart-drawer',
        ignoreDismissed: true,
        namespace: 'cartDrawer',
      })
    },

    /**
     * Shopify section editor select event.
     */
    shopifySectionSelect() {
      if (!this.isMobile) {
        return
      }

      this.openOverlay({
        component: 'menu-drawer',
        ignoreDismissed: true,
        namespace: 'menuDrawer',
      })
    },

    /**
     * Shopify section editor deselect event.
     */
    shopifySectionDeselect() {
      this.closeOverlay('menuDrawer')
    },

    /**
     * Handles opening the mega-nav.
     * @param {Object} tier - The tier object for the mega-nav
     */
    handleNavOpen(tier) {
      if (tier.links.length === 0) {
        window.location.href = tier.url

        return
      }

      clearTimeout(this.navTimeout)

      this.megaNav.currentHandle = tier.handle
      this.megaNav.currentMenu = tier.links
      this.megaNav.open = true

      this.openOverlay({
        component: 'mega-nav',
        ignoreDismissed: true,
        namespace: 'megaNav',
      })
    },
  },
}
</script>
